// KLHS WhatsApp chat (sections/klhs-whatsapp.liquid).
// Opens only on click, knows the current page, and builds a pre-filled wa.me message from the chosen topic.
// The button always reads as online; outside business hours the panel notes that replies may be slower.
if (!customElements.get('klhs-chat')) {
  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MOBILE = window.matchMedia('(max-width: 749px)');

  const parseTime = (value) => {
    const match = /^(\d{1,2}):?(\d{2})?$/.exec((value || '').trim());
    return match ? Number(match[1]) * 60 + Number(match[2] || 0) : null;
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const suffix = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    return mins ? `${hour12}:${String(mins).padStart(2, '0')}${suffix}` : `${hour12}${suffix}`;
  };

  class KlhsChat extends HTMLElement {
    connectedCallback() {
      this.launcher = this.querySelector('.klhs-chat__launcher');
      this.panel = this.querySelector('.klhs-chat__panel');

      this.launcher.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      this.querySelectorAll('[data-chat-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelector('[data-chat-continue]').addEventListener('click', () => this.continue());
      this.addEventListener('keydown', this.onKeyDown.bind(this));
      this.bindSwipe();

      const topics = [...this.querySelectorAll('input[name="klhs-chat-topic"]')];
      if (topics.length && !topics.some((input) => input.checked)) topics[0].checked = true;

      this.updateStatus();
      this.statusTimer = setInterval(() => this.updateStatus(), 60000);
    }

    disconnectedCallback() {
      clearInterval(this.statusTimer);
    }

    get isOpen() {
      return this.launcher.getAttribute('aria-expanded') === 'true';
    }

    // Business hours, evaluated in the shop's time zone rather than the visitor's.
    getHoursState() {
      const offset = Number(this.dataset.utcOffset || 0);
      const forced = this.dataset.forceAway === 'true';
      const openDays = (this.dataset.openDays || '').split(',').filter(Boolean).map(Number);
      const opens = parseTime(this.dataset.openTime);
      const closes = parseTime(this.dataset.closeTime);
      const shopNow = new Date(Date.now() + offset * 3600000);
      const day = shopNow.getUTCDay();
      const minutes = shopNow.getUTCHours() * 60 + shopNow.getUTCMinutes();

      if (opens === null || closes === null || !openDays.length) return { afterHours: forced };

      const afterHours = forced || !openDays.includes(day) || minutes < opens || minutes >= closes;
      if (!afterHours) return { afterHours };

      for (let ahead = 0; ahead <= 7; ahead++) {
        const nextDay = (day + ahead) % 7;
        if (!openDays.includes(nextDay)) continue;
        if (ahead === 0 && (minutes >= opens || forced)) continue;
        const time = formatTime(opens);
        if (ahead === 0) return { afterHours, next: time };
        if (ahead === 1) return { afterHours, next: `tomorrow ${time}` };
        return { afterHours, next: `${DAY_NAMES[nextDay]} ${time}` };
      }
      return { afterHours };
    }

    updateStatus() {
      const { afterHours, next } = this.getHoursState();
      this.afterHours = afterHours;
      this.classList.toggle('klhs-chat--after-hours', afterHours);

      const panelStatus = this.querySelector('[data-panel-status]');
      if (!this.defaultStatus) this.defaultStatus = panelStatus.textContent;
      const fill = (text) => (text || '').replace('{time}', next || 'the next working day').trim();

      panelStatus.textContent = afterHours ? fill(this.dataset.awayStatus) : this.defaultStatus;
      this.querySelectorAll('[data-after-hours]').forEach((el) => (el.hidden = !afterHours));
      const note = this.querySelector('[data-note]');
      if (note) note.textContent = fill(note.dataset.note);

      const cta = this.querySelector('[data-chat-continue]');
      cta.querySelector('[data-cta-label]').textContent = afterHours ? cta.dataset.labelAfterHours : cta.dataset.label;
    }

    // What the customer is looking at: product (with the currently selected variant), collection, or nothing.
    getContext() {
      const type = this.dataset.contextType;
      if (type === 'collection' && this.dataset.contextTitle) {
        return { type, title: this.dataset.contextTitle, label: this.dataset.contextTitle, url: this.dataset.pageUrl };
      }
      if (type !== 'product') return null;

      const data = document.getElementById('KlhsChatProduct');
      if (!data) return null;
      const product = JSON.parse(data.textContent);
      const idInput = document.querySelector('[id^="MainProduct-"] product-form form [name="id"]');
      const variantId = idInput?.value || product.selectedVariantId;
      const variant = product.variants[variantId];
      const variantTitle = product.hasOnlyDefaultVariant || !variant ? '' : variant.title;
      const url = new URL(product.url);
      if (variant && !product.hasOnlyDefaultVariant) url.searchParams.set('variant', variantId);

      return {
        type,
        title: product.title,
        variant: variantTitle,
        label: variantTitle ? `${product.title} · ${variantTitle}` : product.title,
        image: variant?.image || product.image,
        url: url.toString(),
      };
    }

    renderContext() {
      this.context = this.getContext();
      const box = this.querySelector('[data-context]');
      if (!this.context) {
        box.hidden = true;
        return;
      }
      box.hidden = false;
      box.querySelector('[data-context-label]').textContent = this.context.label;
      const image = box.querySelector('[data-context-image]');
      image.hidden = !this.context.image;
      if (this.context.image) image.src = this.context.image;
    }

    open() {
      this.updateStatus();
      this.renderContext();
      this.panel.hidden = false;
      this.launcher.setAttribute('aria-expanded', 'true');
      this.classList.add('klhs-chat--open');
      document.documentElement.classList.toggle('klhs-chat-locked', MOBILE.matches);
      requestAnimationFrame(() => {
        this.classList.add('klhs-chat--visible');
        (this.panel.querySelector('input[name="klhs-chat-topic"]:checked') || this.panel.querySelector('[data-chat-close]'))?.focus();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.launcher.setAttribute('aria-expanded', 'false');
      this.classList.remove('klhs-chat--visible', 'klhs-chat--open');
      this.panel.style.transform = '';
      document.documentElement.classList.remove('klhs-chat-locked');
      setTimeout(() => {
        if (!this.isOpen) this.panel.hidden = true;
      }, 250);
      this.launcher.focus();
    }

    onKeyDown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      // Keep focus inside the panel while it is open.
      const focusable = [...this.panel.querySelectorAll('button, a[href], input:checked, input:not([type="radio"])')].filter(
        (el) => !el.closest('[hidden]') && !el.disabled
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Swipe the bottom sheet down to close it (mobile).
    bindSwipe() {
      const handle = this.querySelector('[data-chat-drag]');
      let startY = null;
      let delta = 0;
      handle.addEventListener('touchstart', (event) => {
        if (!MOBILE.matches) return;
        startY = event.touches[0].clientY;
        delta = 0;
        this.panel.style.transition = 'none';
      }, { passive: true });
      handle.addEventListener('touchmove', (event) => {
        if (startY === null) return;
        delta = Math.max(0, event.touches[0].clientY - startY);
        this.panel.style.transform = `translateY(${delta}px)`;
      }, { passive: true });
      handle.addEventListener('touchend', () => {
        if (startY === null) return;
        this.panel.style.transition = '';
        startY = null;
        if (delta > 80) this.close();
        else this.panel.style.transform = '';
      });
    }

    buildMessage() {
      const topic = this.querySelector('input[name="klhs-chat-topic"]:checked');
      const lines = [topic?.dataset.message || 'Hi KLHS, I have a question.'];
      const context = this.context || this.getContext();

      if (context?.type === 'product') {
        lines.push(`Product: ${context.title}${context.variant ? ` (${context.variant})` : ''}`, context.url);
      } else if (context?.type === 'collection') {
        lines.push(`Browsing: ${context.title}`, context.url);
      } else {
        lines.push(`Page: ${this.dataset.pageUrl}`);
      }
      if (topic?.dataset.prompt) lines.push(topic.dataset.prompt);

      return { text: lines.filter(Boolean).join('\n'), event: topic?.value || 'chat_other', context };
    }

    continue() {
      const { text, event, context } = this.buildMessage();
      const params = {
        page_type: context?.type || 'other',
        product: context?.type === 'product' ? context.title : undefined,
        variant: context?.variant || undefined,
        collection: context?.type === 'collection' ? context.title : undefined,
        after_hours: Boolean(this.afterHours),
      };

      // Shopify customer event (forward to GA4 with a custom pixel), plus gtag if it is loaded on the page.
      try {
        window.Shopify?.analytics?.publish?.(event, params);
        if (typeof window.gtag === 'function') window.gtag('event', event, params);
      } catch (error) {
        console.warn('KLHS chat analytics failed', error);
      }

      window.open(`https://wa.me/${this.dataset.phone}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    }
  }

  customElements.define('klhs-chat', KlhsChat);
}
